<template>
  <div
    class="qty"
    :class="{ disabled }"
    @wheel.prevent="handleWheel"
  >
    <UiIconButton
      class="qty-btn"
      variant="ghost"
      size="sm"
      :disabled="disabled || modelValue <= min"
      :aria-label="`Decrease quantity to ${clamp(modelValue - step)}`"
      @pointerdown="(e) => startHold(e, -1)"
      @click="set(clamp(modelValue - step))"
    >
      {{ ICONS.ui.minus }}
    </UiIconButton>

    <label class="qty-input">
      <span class="sr-only">Quantity</span>
      <input
        class="ui-input stat-mono num"
        type="number"
        inputmode="numeric"
        pattern="[0-9]*"
        :min="min"
        :max="max"
        :step="step"
        :disabled="disabled"
        :value="String(modelValue)"
        @change="handleInputChange"
      />
    </label>

    <UiIconButton
      class="qty-btn"
      variant="ghost"
      size="sm"
      :disabled="disabled || modelValue >= max"
      :aria-label="`Increase quantity to ${clamp(modelValue + step)}`"
      @pointerdown="(e) => startHold(e, 1)"
      @click="set(clamp(modelValue + step))"
    >
      {{ ICONS.ui.plus }}
    </UiIconButton>

    <UiButton
      v-if="showMax"
      class="qty-max"
      size="sm"
      variant="secondary"
      :disabled="disabled || modelValue >= max"
      @click="set(max)"
    >
      <template #icon>{{ ICONS.actions.quantity }}</template>
      Max
    </UiButton>
  </div>

  <div v-if="showSliderResolved" class="qty-slider">
    <input
      class="range"
      type="range"
      :min="min"
      :max="max"
      :step="step"
      :disabled="disabled"
      :value="modelValue"
      @input="handleSliderInput"
    />
    <div class="qty-slider__meta text-muted stat-mono num">
      <span>{{ min }}</span>
      <span>{{ max }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount } from "vue";
import { ICONS } from "../../lib/icons";
import UiButton from "./UiButton.vue";
import UiIconButton from "./UiIconButton.vue";

const props = withDefaults(
  defineProps<{
    modelValue: number;
    min?: number;
    max?: number;
    step?: number;
    disabled?: boolean;
    showMax?: boolean;
    showSlider?: boolean | "auto";
  }>(),
  {
    min: 1,
    max: 1,
    step: 1,
    disabled: false,
    showMax: true,
    showSlider: "auto"
  }
);

const emit = defineEmits<{
  "update:modelValue": [value: number];
}>();

function clamp(value: number): number {
  const min = Math.floor(Number(props.min ?? 1));
  const max = Math.floor(Number(props.max ?? min));
  const step = Math.max(1, Math.floor(Number(props.step ?? 1)));
  const v = Math.floor(Number(value));
  const clamped = Math.max(min, Math.min(max, Number.isFinite(v) ? v : min));
  const snapped = min + Math.round((clamped - min) / step) * step;
  return Math.max(min, Math.min(max, snapped));
}

function set(value: number) {
  emit("update:modelValue", clamp(value));
}

const showSliderResolved = computed(() => {
  if (props.showSlider === true) return true;
  if (props.showSlider === false) return false;
  const range = Math.max(0, (props.max ?? 1) - (props.min ?? 1));
  return range >= 24;
});

function handleInputChange(event: Event) {
  const el = event.target as HTMLInputElement;
  set(Number(el.value));
}

function handleSliderInput(event: Event) {
  const el = event.target as HTMLInputElement;
  set(Number(el.value));
}

function handleWheel(event: WheelEvent) {
  if (props.disabled) return;
  const dir = event.deltaY > 0 ? -1 : 1;
  set(props.modelValue + dir * (props.step ?? 1));
}

let holdTimer: number | null = null;
let holdStartTimer: number | null = null;
let activePointerId: number | null = null;

function stopHold() {
  if (holdStartTimer) window.clearTimeout(holdStartTimer);
  if (holdTimer) window.clearInterval(holdTimer);
  holdStartTimer = null;
  holdTimer = null;
  activePointerId = null;
  window.removeEventListener("pointerup", stopHold);
  window.removeEventListener("pointercancel", stopHold);
}

function startHold(event: PointerEvent, direction: -1 | 1) {
  if (props.disabled) return;
  if (activePointerId !== null) return;
  activePointerId = event.pointerId;

  const step = Math.max(1, Math.floor(Number(props.step ?? 1)));
  const tick = () => set(props.modelValue + direction * step);

  tick();
  holdStartTimer = window.setTimeout(() => {
    holdTimer = window.setInterval(tick, 70);
  }, 260);

  window.addEventListener("pointerup", stopHold);
  window.addEventListener("pointercancel", stopHold);
}

onBeforeUnmount(() => stopHold());
</script>

<style scoped>
.qty {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  flex-wrap: wrap;
}

.qty.disabled {
  opacity: 0.7;
}

.qty-input {
  display: grid;
}

.qty-input input {
  width: 6.5rem;
  text-align: center;
}

.qty-btn {
  width: 36px;
  height: 36px;
}

.qty-max {
  height: 36px;
}

.qty-slider {
  margin-top: 0.55rem;
  display: grid;
  gap: 0.35rem;
}

.range {
  width: 100%;
}

.qty-slider__meta {
  display: flex;
  justify-content: space-between;
  font-size: 0.8rem;
}
</style>

